//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DragAndDrop } from 'unison';
import { Alert } from '@components/atoms/alert/Alert';
import { ConnectPanelActions } from '@catalog-ingestion/actions/connectPanelActions';
import { ICuralateStore } from '@models/AppState';
import styled from 'styled-components';
import { ReduxDispatch } from 'app/curalate.store';
import { BAZAARVOICE_CONTACT_SUPPORT_URL } from 'app/constants/support-links';

function mapStateToProps(state: ICuralateStore) {
  return {
    catalogUrl: state.catalogIngestionReducers.connectPanel.catalogUrl,
    fileUploadFailed:
      state.catalogIngestionReducers.connectPanel.didFileUploadFail,
    isfileUploading:
      state.catalogIngestionReducers.connectPanel.isFileUploading,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  return {
    actions: {
      connect: bindActionCreators(ConnectPanelActions, dispatch),
    },
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
export const FileField = (props: Props) => {
  const [isFileTooLarge, setIsFileTooLarge] = useState(false);

  const handleNewFile = async (newFile: File) => {
    const actions = props.actions.connect;
    // block upload if the file size is over 50 MB
    const MAX_CATALOG_UPLOAD_SIZE_MB = 50 * 1024 * 1024;
    if (newFile.size > MAX_CATALOG_UPLOAD_SIZE_MB) {
      setIsFileTooLarge(true);
      actions.setDidFileUploadFail(true);
    } else {
      const res = await actions.uploadFileToS3(newFile);
      actions.changeCatalogUrl(res.toString());
      actions.setIsFileUploading(false);
      actions.setIsUrlEmpty(false);
    }
  };

  return (
    <div>
      <FileUploadHeader>Upload a File</FileUploadHeader>
      {!props.fileUploadFailed ? (
        <StyledDragAndDrop handleUploadedFile={handleNewFile} />
      ) : (
        <>
          {isFileTooLarge ? (
            <Alert type='error' className='cu-mb-3'>
              This catalog upload method only supports files that are up to 50
              MB. Try to compress your catalog or select 'Use a Link' to upload
              a catalog via a link. For questions, contact{' '}
              <a
                target='_blank'
                href={BAZAARVOICE_CONTACT_SUPPORT_URL}
                rel='noreferrer'
              >
                Bazaarvoice Support
              </a>
              .
            </Alert>
          ) : (
            <Alert type='error' className='cu-mb-3'>
              There was an unexpected error while uploading your file. Try again
              later, and if the error persists, contact{' '}
              <a
                target='_blank'
                href={BAZAARVOICE_CONTACT_SUPPORT_URL}
                rel='noreferrer'
              >
                Bazaarvoice Support
              </a>
            </Alert>
          )}
          <StyledDragAndDrop handleUploadedFile={handleNewFile} />
        </>
      )}
    </div>
  );
};

const StyledDragAndDrop = styled(DragAndDrop)`
  div.cu-drag-and-drop-text > label {
    display: inline;
  }
`;

const FileUploadHeader = styled.div.attrs({
  className: 'cu-text-subsection',
})`
  padding-bottom: ${({ theme }) => theme.spacings[1]};
`;

export default connect(mapStateToProps, mapDispatchToProps)(FileField);
